import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { skillsApi, categoriesApi } from '../utils/api';
import GlassCard from '../components/GlassCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, Save, Eye, ArrowLeft, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const SkillBuilderPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fromDiscovery, setFromDiscovery] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    instructions: '',
    skill_md: '',
    visibility: 'public',
    // Advanced fields from discovery
    primaryFunction: '',
    idealFor: '',
    purpose: '',
    keyCapabilities: [],
    triggerPhrases: []
  });

  useEffect(() => {
    loadCategories();

    // SUPER SIMPLE - Check localStorage immediately
    const storedData = localStorage.getItem('DISCOVERY_DATA');
    
    if (storedData) {
      alert('Found data! Loading...');
      console.log('Found stored data:', storedData);
      
      try {
        const data = JSON.parse(storedData);
        console.log('Parsed data:', data);
        
        // Set form data directly
        setFormData({
          ...formData,
          name: data.name || '',
          description: data.description || '',
          instructions: data.instructions || '',
          primaryFunction: data.primaryFunction || '',
          idealFor: data.idealFor || '',
          purpose: data.purpose || '',
          keyCapabilities: data.keyCapabilities || []
        });
        
        setFromDiscovery(true);
        setShowAdvanced(true);
        
        // Clear it
        localStorage.removeItem('DISCOVERY_DATA');
        
        alert('Data loaded into form!');
      } catch (e) {
        alert('Parse failed: ' + e.message);
      }
    } else {
      console.log('No stored data found');
    }

    // Regular loading
    if (id) {
      loadSkill();
    }
  }, [id]);

  const loadFromDiscovery = () => {
    console.log('🔍 SKILL BUILDER - ATTEMPTING TO LOAD FROM DISCOVERY');
    console.log('Current URL:', window.location.href);
    console.log('Search params:', window.location.search);

    // Try multiple times in case of timing issue
    let attempts = 0;
    const maxAttempts = 5;

    const tryLoad = () => {
      attempts++;
      console.log(`🔄 Attempt ${attempts}/${maxAttempts}`);

      // Check BOTH keys in case of legacy code
      const conceptData = sessionStorage.getItem('skillConcept') || sessionStorage.getItem('discoveredSkill');
      console.log('Raw data from sessionStorage:', conceptData ? `EXISTS (${conceptData.length} chars)` : 'NULL');

      if (conceptData) {
        try {
          const concept = JSON.parse(conceptData);
          console.log('✅ PARSED CONCEPT:', concept);
          console.log('Concept keys:', Object.keys(concept));

          // Pre-populate ALL form fields
          const newFormData = {
            ...formData,
            name: concept.skillName || concept.displayName || '',
            description: concept.description || '',
            instructions: concept.primaryFunction || concept.purpose || '',
            primaryFunction: concept.primaryFunction || '',
            idealFor: concept.idealFor || '',
            purpose: concept.purpose || '',
            keyCapabilities: Array.isArray(concept.keyCapabilities) ? concept.keyCapabilities : [],
            triggerPhrases: Array.isArray(concept.triggerPhrases) ? concept.triggerPhrases : []
          };

          console.log('📝 Setting form data:', newFormData);
          setFormData(newFormData);
          setFromDiscovery(true);

          // Auto-expand advanced section if we have advanced data
          if (concept.primaryFunction || concept.idealFor || concept.purpose) {
            console.log('✅ Auto-expanding advanced section');
            setShowAdvanced(true);
          }

          // Clear from sessionStorage so it doesn't reload on refresh
          sessionStorage.removeItem('skillConcept');
          sessionStorage.removeItem('discoveredSkill');
          console.log('🧹 Cleared sessionStorage');

          console.log('🎉 CONCEPT LOADED SUCCESSFULLY');
          console.log('Form now has:');
          console.log('  - name:', newFormData.name);
          console.log('  - description:', newFormData.description);
          console.log('  - primaryFunction:', newFormData.primaryFunction);
          console.log('  - idealFor:', newFormData.idealFor);
          console.log('  - keyCapabilities:', newFormData.keyCapabilities.length, 'items');

        } catch (error) {
          console.error('❌ Error parsing concept:', error);
          console.error('Error details:', error.message);
        }
      } else {
        console.warn(`⚠️ Attempt ${attempts}: No data in sessionStorage yet`);

        // Retry if under max attempts
        if (attempts < maxAttempts) {
          console.log(`⏳ Waiting 200ms before retry...`);
          setTimeout(tryLoad, 200);
        } else {
          console.error('❌ FAILED TO LOAD AFTER ALL ATTEMPTS');
          console.error('SessionStorage contents:', Object.keys(sessionStorage));
        }
      }
    };

    // Start loading
    tryLoad();
  };

  const loadCategories = async () => {
    try {
      const res = await categoriesApi.list();
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  };

  const loadSkill = async () => {
    try {
      setLoading(true);
      const res = await skillsApi.get(id);
      setFormData(res.data);
    } catch (error) {
      toast.error('Failed to load skill');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddCapability = () => {
    setFormData({
      ...formData,
      keyCapabilities: [...formData.keyCapabilities, '']
    });
  };

  const handleUpdateCapability = (index, value) => {
    const newCapabilities = [...formData.keyCapabilities];
    newCapabilities[index] = value;
    setFormData({ ...formData, keyCapabilities: newCapabilities });
  };

  const handleRemoveCapability = (index) => {
    const newCapabilities = formData.keyCapabilities.filter((_, i) => i !== index);
    setFormData({ ...formData, keyCapabilities: newCapabilities });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (id) {
        await skillsApi.update(id, formData);
        toast.success('Skill updated successfully!');
      } else {
        const res = await skillsApi.create(formData);
        toast.success('Skill created successfully!');
        navigate(`/skills/${res.data.id}`);
      }
    } catch (error) {
      toast.error('Failed to save skill');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-text-gray dark:text-dark-text-secondary hover:text-text-dark dark:hover:text-dark-text mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-4xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
            {id ? 'Edit Skill' : 'Create New Skill'}
          </h1>
          <p className="text-lg text-text-gray dark:text-dark-text-secondary">
            Build your Claude Skill with our intuitive editor
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="rounded-xl glass"
            onClick={() => navigate('/dashboard')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || !formData.name}
            className="gradient-primary text-white rounded-xl shadow-lg hover:shadow-xl"
          >
            <Save className="mr-2 h-4 w-4" />
            {loading ? 'Saving...' : id ? 'Update' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* Success Banner from Discovery */}
      {fromDiscovery && (
        <div className="p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-green-800 dark:text-green-400">
              Skill concept loaded from Discovery Session!
            </p>
            <p className="text-sm text-green-700 dark:text-green-500 mt-1">
              All available information has been pre-filled. Review below and continue.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Basic Info Card */}
        <GlassCard variant="strong" className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-primary/20 to-primary-dark/20 rounded-lg">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-manrope font-bold text-text-dark dark:text-dark-text">
                Basic Information
              </h2>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                Tell us about your skill
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <Label className="text-sm font-semibold mb-2">Skill Name *</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Content Writer Pro"
                required
                className="glass border-0"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2">Description *</Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe what your skill does..."
                rows={3}
                required
                className="glass border-0"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2">Category *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger className="glass border-0">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="glass-strong">
                  {categories.map((cat) => (
                    <SelectItem key={cat.slug} value={cat.slug}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </GlassCard>

        {/* Instructions Card */}
        <GlassCard variant="strong" className="p-8">
          <div className="mb-6">
            <h3 className="text-xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
              Instructions
            </h3>
            <p className="text-sm text-text-gray dark:text-dark-text-secondary">
              How should Claude use this skill?
            </p>
          </div>

          <Textarea
            name="instructions"
            value={formData.instructions}
            onChange={handleChange}
            placeholder="Enter detailed instructions for Claude..."
            rows={8}
            className="glass border-0 font-mono text-sm"
          />
        </GlassCard>

        {/* Advanced Details Card */}
        <GlassCard variant="strong" className="p-8">
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-primary hover:text-primary-dark transition-colors"
            >
              <Sparkles className="h-5 w-5" />
              <h3 className="text-xl font-manrope font-bold">
                {showAdvanced ? 'Hide' : 'Show'} Advanced Details
              </h3>
              {fromDiscovery && !showAdvanced && (
                <span className="ml-2 px-2 py-1 bg-primary/10 text-primary rounded text-xs font-semibold">
                  From Discovery
                </span>
              )}
            </button>
            <p className="text-sm text-text-gray dark:text-dark-text-secondary mt-2">
              Additional metadata populated from Discovery Session
            </p>
          </div>

          {showAdvanced && (
            <div className="space-y-6 pt-6 border-t dark:border-[#2e2e2d]">
              {/* Primary Function */}
              <div>
                <Label className="text-sm font-semibold mb-2">Primary Function</Label>
                <Textarea
                  name="primaryFunction"
                  value={formData.primaryFunction}
                  onChange={handleChange}
                  placeholder="What is the main task or workflow this skill handles?"
                  rows={3}
                  className="glass border-0"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.primaryFunction.length}/500 characters
                </p>
              </div>

              {/* Ideal For */}
              <div>
                <Label className="text-sm font-semibold mb-2">Ideal For</Label>
                <Input
                  name="idealFor"
                  value={formData.idealFor}
                  onChange={handleChange}
                  placeholder="e.g., Product managers building roadmaps"
                  className="glass border-0"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Target user type and use case
                </p>
              </div>

              {/* Purpose */}
              <div>
                <Label className="text-sm font-semibold mb-2">Purpose</Label>
                <Textarea
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  placeholder="Why does this skill exist? What problem does it solve?"
                  rows={3}
                  className="glass border-0"
                />
              </div>

              {/* Key Capabilities */}
              <div>
                <Label className="text-sm font-semibold mb-3">Key Capabilities</Label>
                <div className="space-y-2">
                  {formData.keyCapabilities.map((capability, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={capability}
                        onChange={(e) => handleUpdateCapability(index, e.target.value)}
                        placeholder="e.g., Extracts key frameworks from PDFs"
                        className="glass border-0 flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleRemoveCapability(index)}
                        className="px-3 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddCapability}
                    className="w-full glass border-dashed"
                  >
                    + Add Capability
                  </Button>
                </div>
              </div>
            </div>
          )}
        </GlassCard>

        {/* SKILL.md Card */}
        <GlassCard variant="strong" className="p-8">
          <div className="mb-6">
            <h3 className="text-xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
              SKILL.md Content
            </h3>
            <p className="text-sm text-text-gray dark:text-dark-text-secondary">
              Advanced: Edit the raw SKILL.md format
            </p>
          </div>

          <Textarea
            name="skill_md"
            value={formData.skill_md}
            onChange={handleChange}
            placeholder="# Skill Name\n\n## Instructions\n...\n"
            rows={12}
            className="glass border-0 font-mono text-sm"
          />
        </GlassCard>

        {/* Visibility Card */}
        <GlassCard variant="strong" className="p-8">
          <div className="mb-6">
            <h3 className="text-xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
              Visibility
            </h3>
            <p className="text-sm text-text-gray dark:text-dark-text-secondary">
              Who can see this skill?
            </p>
          </div>

          <Select
            value={formData.visibility}
            onValueChange={(value) => setFormData({ ...formData, visibility: value })}
          >
            <SelectTrigger className="glass border-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="glass-strong">
              <SelectItem value="public">Public - Anyone can see</SelectItem>
              <SelectItem value="unlisted">Unlisted - Only with link</SelectItem>
              <SelectItem value="private">Private - Only you</SelectItem>
            </SelectContent>
          </Select>
        </GlassCard>
      </form>
    </div>
  );
};

export default SkillBuilderPage;